import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowPathIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import useLockboxDetails from './useLockboxDetails';
import LockboxDetailsSheet from './LockboxDetailsSheet';

function SpinnerOverlay({ visible, message, onDismiss, processing }) {
  if (!visible) return null;

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/30 backdrop-blur-xl">
      <div className="rounded-3xl border border-white/20 bg-white/80 px-8 py-8 text-center shadow-[0_20px_60px_rgba(15,23,42,0.25)]">
        {processing ? (
          <div className="flex flex-col items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-full border border-slate-200/80 bg-white/80 shadow-inner">
              <ArrowPathIcon className="h-6 w-6 animate-spin text-slate-500" aria-hidden="true" />
            </div>
            <p className="text-sm font-medium text-slate-600">{message || 'Fetching lockbox details…'}</p>
          </div>
        ) : (
          <div className="flex max-w-sm flex-col items-center gap-4 text-slate-600">
            <ExclamationCircleIcon className="h-8 w-8 text-amber-500" aria-hidden="true" />
            <p className="whitespace-pre-wrap text-sm leading-relaxed">{message}</p>
            <button
              type="button"
              onClick={onDismiss}
              className="rounded-full border border-slate-200/80 bg-white/70 px-4 py-2 text-xs font-medium uppercase tracking-[0.16em] text-slate-600 shadow hover:border-slate-300 hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60"
            >
              Dismiss
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

/**
 * LockboxDetailsLauncher renders the trigger button, handles fetch state,
 * and presents the lockbox sheet once data is loaded.
 */
export default function LockboxDetailsLauncher({
  orderId,
  propertyLabel,
  propertyId,
  className,
  children,
  buttonText = 'View Lockbox Details',
  autoCloseOnCopy = false,
}) {
  const { data, error, fetchDetails, isLoading, isError, isSuccess, reset } = useLockboxDetails();
  const [open, setOpen] = useState(false);
  const [copyToast, setCopyToast] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [overlayMessage, setOverlayMessage] = useState('');

  useEffect(() => {
    if (isLoading) {
      setOverlayMessage('Fetching lockbox details…');
      setShowOverlay(true);
    } else if (!isLoading && !isError) {
      setShowOverlay(false);
    }
  }, [isLoading, isError]);

  useEffect(() => {
    if (isSuccess && data) {
      setOpen(true);
    }
  }, [isSuccess, data]);

  useEffect(() => {
    if (isError && error) {
      setOverlayMessage(typeof error === 'string' ? error : 'Unable to load lockbox details.');
      setShowOverlay(true);
    }
  }, [isError, error]);

  const derivedPropertyLabel = useMemo(() => {
    if (propertyLabel) return propertyLabel;
    if (data?.propertyName) return data.propertyName;
    if (propertyId) return `Property ${propertyId}`;
    if (data?.propertyId) return `Property ${data.propertyId}`;
    return undefined;
  }, [data, propertyLabel, propertyId]);

  const handleRequest = () => {
    setOverlayMessage('Fetching lockbox details…');
    setShowOverlay(true);
    setCopyToast(false);
    fetchDetails(orderId).catch(() => {
      // Error handled in isError effect; nothing extra here
    });
  };

  const handleClose = () => {
    setOpen(false);
    setCopyToast(false);
    reset();
  };

  const handleCopy = async (code) => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopyToast(true);
      setTimeout(() => setCopyToast(false), 2200);
      if (autoCloseOnCopy) {
        handleClose();
      }
    } catch (_) {
      setOverlayMessage('Copy failed. Please try again.');
      setShowOverlay(true);
    }
  };

  const dismissOverlay = () => {
    setShowOverlay(false);
    if (isError) {
      reset();
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleRequest}
        className={classNames(
          'inline-flex items-center gap-2 rounded-full border border-slate-200/80 bg-white/70 px-4 py-2 text-sm font-medium text-slate-600 shadow-sm transition hover:border-slate-300 hover:bg-white hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300',
          className
        )}
      >
        {children || buttonText}
      </button>

      <SpinnerOverlay
        visible={showOverlay}
        message={overlayMessage}
        processing={isLoading}
        onDismiss={dismissOverlay}
      />

      <LockboxDetailsSheet
        open={open}
        onClose={handleClose}
        data={data}
        propertyLabel={derivedPropertyLabel}
        copyToast={copyToast}
        onCopyCode={handleCopy}
      />
    </>
  );
}
